import { createReference } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';
import { WonHistoryEvent } from '@bariendo/lib/payment-status';
import { reportError } from '@/services/error-reporting';
import { fetchBot } from '@/services/services';

export interface PanelPaymentHistory {
  /** Every Won and reversal. Undefined until read, when not allowed, or when the read failed. */
  wonEvents?: WonHistoryEvent[];
}

interface State extends PanelPaymentHistory {
  patientId: string;
}

/**
 * The patient's Won history, for the panel's Won date: a Won stays on the panel after a
 * cancellation reverses it, and the stored payment status no longer says when it was. Refund
 * requests come from the page's one read instead (`refundRequestsContext`).
 *
 * `enabled` is the payments-view gate: the bot refuses everyone else, so they are not asked. A
 * failed read leaves it undefined; the row that needs it falls back to the stored status.
 * `statusKey` is the stored payment status: a Won or a reversal moves it, so a change re-reads.
 */
export function usePanelPaymentHistory(
  patient: Patient | undefined,
  enabled: boolean,
  statusKey: string | undefined
): PanelPaymentHistory {
  const medplum = useMedplum();
  const [state, setState] = useState<State>();
  const patientId = patient?.id;

  useEffect(() => {
    if (!enabled || !patient || !patientId) return undefined;
    let cancelled = false;
    const patients = [createReference(patient)];

    const read = async (): Promise<void> => {
      try {
        const won = await fetchBot<{ events: WonHistoryEvent[] }>(medplum, 'create-treatment-payment', {
          operation: 'wonHistory',
          patients,
        });
        if (!cancelled) setState({ patientId, wonEvents: won.events });
      } catch (error) {
        reportError(error, { component: 'usePanelPaymentHistory' });
        if (!cancelled) setState({ patientId });
      }
    };
    void read();
    return () => {
      cancelled = true;
    };
    // Keyed on the id: a re-read Patient resource is the same patient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medplum, patientId, enabled, statusKey]);

  if (!enabled || !state || state.patientId !== patientId) return {};
  return { wonEvents: state.wonEvents };
}
